import { Component, EventEmitter, Input, Output } from '@angular/core';
import { openClose } from '@core/animations/animations';
import {
	FinancialInstitutionLender,
	Lender,
	LenderArgs,
	LenderClientType,
	LenderClientTypeArgs,
	LenderSubType,
	LenderWithAssociations,
	MortgageeType,
} from '@models/lender';
import { onSelectInstitution } from '../searchable-fields/lender-search-input/lender-search-input.types';
import { MatterMortgage } from '@models/mortgage';
import { ClientsRepoService } from '@repos/clients/clients.service';
import { ClientCreationData, PersonClient, Client } from '@models/clients';
import { InstitutionRepo } from '@repos/institution/institution.service';
import { UntilDestroy } from '@ngneat/until-destroy';
import LogRocket from 'logrocket';
import { getMortgageLenders } from '@core/util/mortgage';
import { ClientsDispatch } from '@state/clients';
import { MatterClientType } from '@models/matter';
import { ClientCreationService } from '@services/closer-api/client-creation/client-creation.service';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep, isEqual } from 'lodash';
import { SearchType } from 'src/app/public/leap/leap-api.service';
import {
	MortgageTypeOptions,
	computerShareInstitutionId,
} from './lender-selector.types';

@UntilDestroy()
@Component({
	selector: 'app-lender-selector',
	templateUrl: './lender-selector.component.html',
	animations: [openClose()],

	standalone: false,
})
export class LenderSelectorComponent {
	@SelectSnapshot(SelectedMatter.id) matterId: string;

	public mortgageeTypeOptions = MortgageTypeOptions;
	public selectedMortgageeType: MortgageeType;
	public mortgageeTypes = MortgageeType;
	public computerShareInstitution: FinancialInstitutionLender;
	public lenders: (Lender | LenderWithAssociations)[] = [];

	@Input() set mortgage(mortgage: MatterMortgage) {
		// Only one BANK or COMPUTERSHARE lender can be added to a mortgage
		const prevLenders = cloneDeep(this.lenders);
		this.lenders = getMortgageLenders(mortgage);
		const hasFinancialInstitutionLender = !!this.lenders.find(
			(lender) =>
				lender.mortgageeType === MortgageeType.INSTITUTION ||
				lender.mortgageeType === MortgageeType.COMPUTERSHARE,
		);
		const hasPrivateLender = !!this.lenders.find(
			(lender) => lender.mortgageeType === MortgageeType.PRIVATE_LENDER,
		);

		//TODO: REMOVE THIS ONCE ADDRESS SEARCH STOPS TRIGGERING MORTGAGE UPDATES ON SEARCH INPUT
		if (!this.lenders.length || !isEqual(prevLenders, this.lenders)) {
			this.mortgageeTypeOptions = hasFinancialInstitutionLender
				? MortgageTypeOptions.map((option) => {
						return {
							...option,
							disabled:
								(hasFinancialInstitutionLender &&
									(option.value === MortgageeType.INSTITUTION ||
										option.value === MortgageeType.COMPUTERSHARE)) ??
								false,
						};
					})
				: MortgageTypeOptions;

			const defaultOption = hasPrivateLender
				? this.mortgageeTypeOptions.find(
						(option) => option.value === MortgageeType.PRIVATE_LENDER,
					).value
				: this.mortgageeTypeOptions.find((option) => !option.disabled).value;

			this.setSelectedMortgageeType(defaultOption);
		}
	}

	@Output() onSelectLender = new EventEmitter<LenderArgs>();

	constructor(
		private clientRepo: ClientsRepoService,
		private institutionRepo: InstitutionRepo,
		private clientsDispatch: ClientsDispatch,
		private clientCreationService: ClientCreationService,
	) {}

	public SearchType = SearchType;

	public async setSelectedMortgageeType(value: MortgageeType) {
		this.selectedMortgageeType = value;

		// if mortgagee type is computer share set computershare as the institution
		if (value === MortgageeType.COMPUTERSHARE) {
			try {
				const _doc = await this.institutionRepo
					.Get(computerShareInstitutionId)
					.toPromise();
				this.computerShareInstitution = {
					id: computerShareInstitutionId,
					...(_doc.data() as FinancialInstitutionLender),
					institutionNumber: computerShareInstitutionId,
					logoUrl: _doc.data().logoUrl ?? null,
					branch: null,
				};
			} catch (err) {
				LogRocket.error(
					'Unable to select financial institution to ComputerShare',
					err,
				);
				console.log(
					'Unable to select financial institution to ComputerShare',
					err,
				);
			}
		}
	}

	selectLender(
		lender: Partial<Lender>,
		mortgageeType: MortgageeType,
		mortgageeSubType: LenderSubType,
		args: LenderClientTypeArgs = null,
	) {
		this.onSelectLender.emit({
			lender: {
				...lender,
				mortgageeType: mortgageeType,
				mortgageeSubType: mortgageeSubType,
			},
			args: args,
		});
	}

	onFinancialInstitutionSelected(event: onSelectInstitution) {
		if (this.selectedMortgageeType === MortgageeType.COMPUTERSHARE) {
			this.selectLender(
				{
					...this.computerShareInstitution,
					branch: event.financialInstitution.branch,
				},
				MortgageeType.COMPUTERSHARE,
				'institution',
			);
		} else if (this.selectedMortgageeType === MortgageeType.INSTITUTION) {
			this.selectLender(
				event.financialInstitution,
				this.selectedMortgageeType,
				'institution',
			);
		}
	}

	async onClientSelected($event: Client) {
		const lender: Lender = {
			mortgageeSubType: undefined,
			mortgageeType: undefined,
			clientId: $event.id,
		};

		const args: LenderClientTypeArgs = {
			isPerson: !$event.leap?.cardId,
			alreadyAddedToMatter: false,
			leap: $event.leap,
		};

		this.selectLender(lender, MortgageeType.PRIVATE_LENDER, $event.type, args);
	}

	async onAddClient(clientCreationData: ClientCreationData) {
		await this.clientCreationService
			.AddNewClientToMatter(
				{
					matterClientType: MatterClientType.PRIVATE_LENDER,
					matterId: this.matterId,
				},
				{
					type: clientCreationData.client.type,
					id: clientCreationData.client.id,
					name: clientCreationData.client.name,
					firstName: (clientCreationData.client as PersonClient).firstName,
					middleNames: (clientCreationData.client as PersonClient).middleNames,
					lastName: (clientCreationData.client as PersonClient).lastName,
					email: (clientCreationData.client as PersonClient).email,
					gender: (clientCreationData.client as PersonClient).gender,
					phone: (clientCreationData.client as PersonClient).phone,
					title: (clientCreationData.client as PersonClient).title,
					representation: (clientCreationData.client as PersonClient)
						.representation,
					isLeap: clientCreationData.client.isLeap,
					leap: clientCreationData.client.leap,
				},
			)
			.toPromise();

		const lender: Partial<LenderClientType> = {
			clientId: clientCreationData.client.id,
		};

		const args: LenderClientTypeArgs = {
			isPerson: false,
			leap: null,
			alreadyAddedToMatter: true,
		};

		this.selectLender(
			lender,
			MortgageeType.PRIVATE_LENDER,
			clientCreationData.client.type,
			args,
		);
	}
}
